<template>
    <v-form ref="editSpotCheckForm" v-model="editSpotCheckValid">
        <v-container fluid>
            <v-layout row justify-center>
                <v-flex xs12 sm6 lg3>
                    <v-textarea
                        v-model="editedSpotCheck.Notes"
                        label="Spot Check Notes">
                    </v-textarea>
                </v-flex>
                <v-flex xs12 sm6 lg3>
                    <v-textarea
                        v-model="editedSpotCheck.Label"
                        label="Spot Check Label">
                    </v-textarea>
                </v-flex>
            </v-layout>
            <v-layout row justify-center>
                <v-flex xs12 sm6 lg3>
                    <v-text-field v-model="search" append-icon="search" label="Search" clearable single-line hide-details></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 lg3>
                    <v-layout row justify-end>
                        <v-btn color="accent" @click="showAllPest">All<v-icon v-if="showAll" right dark>check_box</v-icon><v-icon v-if="!showAll" right dark>check_box_outline_blank</v-icon></v-btn>
                        <v-btn v-if="(defaultScoutingScoreScale != null)" color="accent" @click="showOtherPest = true">Add Pest</v-btn>
                    </v-layout>
                </v-flex>
            </v-layout>
            <v-layout row justify-center>
                <v-flex xs12 lg6>
                    <v-data-table 
                      :headers="computedHeaders" 
                      :items="filteredPestConfigurations" 
                      :search="search"
                      :rows-per-page-items="rowsPerPageItems"
                      :pagination.sync="pagination">
                        <template v-slot:items="{ item }">
                            <ScoutingSpotCheckObservationRowVue
                                :imageAuthToken="imageAuthToken"
                                :observation="editedSpotCheckObservations[item.PestConfigurationID].Observation"
                                :time="editedSpotCheckObservations[item.PestConfigurationID].Time"
                                :notes="editedSpotCheckObservations[item.PestConfigurationID].Notes"
                                :otherName="editedSpotCheckObservations[item.PestConfigurationID].OtherName"
                                :size="editedSpotCheckObservations[item.PestConfigurationID].Size"
                                :latitude="editedSpotCheckObservations[item.PestConfigurationID].Latitude"
                                :longitude="editedSpotCheckObservations[item.PestConfigurationID].Longitude"                                
                                @onNotesChanged="notesChanged"
                                @onObservationChanged="observationChanged"
                                @onSizeChanged="sizeChanged"
                                @onGPSChanged="gPSChanged"
                                :pestConfiguration="item"
                                :locationID="locationID"
                            >
                            </ScoutingSpotCheckObservationRowVue>
                        </template>
                    </v-data-table>
                </v-flex>
            </v-layout>
        </v-container>
        <ScoutSpotCheckObservationOtherPestDialogVue :dialog="showOtherPest" :scoutingScoreScale="defaultScoutingScoreScale" @close="showOtherPest = false" @save="addNewOtherPest"></ScoutSpotCheckObservationOtherPestDialogVue>
    </v-form>
</template>

<script>
import ScoutingSpotCheckObservationRowVue from "@/components/scout/ScoutingSpotCheckObservationRow.vue";
import ScoutSpotCheckObservationOtherPestDialogVue from "@/components/scout/ScoutSpotCheckObservationOtherPestDialog.vue";
import moment from "moment";
import pestConfigurationApi from "@/api/pestConfigurationApi";

export default {
    data() {
        return {
            processing: false,
            showOtherPest: false,
            showAll: false,
            imageAuthToken: null,
            debug: false,
            editedSpotCheck: null,
            unsavedChanges: false,
            editSpotCheckValid: false,
            negativeIndexForObservationsWithoutPestConfigurationData: -1,            
            initialDataCopy: null,
            editedSpotCheckObservations: {},
            search: "",
            classificationSizes: ["Small", "Medium", "Large"],
            headers: [
                {
                    text: "Classification",
                    value: "PestClassification",
                    sortable: true,
                    class: "pest-configs-header"
                },
                {
                    text: "Type",
                    value: "PestClassificationType",
                    sortable: true,
                    class: "pest-configs-header",
                    hide: 'smAndDown'                                 
                },
                {
                    text: "Name",
                    value: "Name",                    
                    sortable: true,
                    class: "pest-configs-header"
                },
                {
                    text: "Size",
                    value: "Size",                    
                    sortable: false,
                    class: "pest-configs-header"
                },
                {
                    text: "Score",
                    value: "Score",                    
                    sortable: true,
                    class: "pest-configs-header"
                },
                {
                    text: "Other",                    
                    sortable: false,
                    class: "pest-configs-header"
                }
            ],
            pagination: {
                page: 1,
                rowsPerPage: 15,
                sortBy: '',
            },
            rowsPerPageItems: [15, 25, 50, 100],
            top10Observations: [],
        }
    },
    props: [
        "initialData",
        "editedScouting"
    ],
    components: {
        ScoutingSpotCheckObservationRowVue,
        ScoutSpotCheckObservationOtherPestDialogVue
    },
    watch: {
        editedSpotCheck: {
            handler(newVal) {
                if (this.debug) console.log(`this.editedSpotCheck changed to  :`, JSON.stringify(newVal));

                // only check for changes if there are not already unsaved changes
                if (!this.unsavedChanges) {
                    // the object is different to the initial data for the control
                    if (JSON.stringify(this.initialDataCopy) != JSON.stringify(newVal)) {
                        this.unsavedChanges = true;

                        this.$emit("onUnsavedChanges");
                    }
                }
            },
            deep: true
        }
    },
    computed: {
        locationID() {
            return this.editedScouting && this.editedScouting.LocationID ? this.editedScouting.LocationID : null;
        },
        computedHeaders () {
            return this.headers.filter(h => !h.hide || !this.$vuetify.breakpoint[h.hide])  
        },
        /**
         * Returns pest configurations that match any of the following:
         * - active and applies in the current plant
         * - has an observation value
         */
        filteredPestConfigurations() {
            // all of the active pest configurations for this plant, whether we have values for them or not
            let plantConfigs = this.$store.getters["ScoutStore/getPestConfigurationsForPlant"](this.currentPlantID);
            
            // This featureworks only in online mode
            const allActivePlantConfigs = this.$store.getters["ScoutStore/getAllActivePestConfigurations"];
            const safeTop10Observations = Array.isArray(this.top10Observations) 
                ? this.top10Observations 
                : [];
            const top10PlantConfigs = allActivePlantConfigs.filter(pc =>
                safeTop10Observations.some(o =>
                    o.PestConfigurationID === pc.PestConfigurationID));

            if (this.showAll) {
                let nonPlantConfigs = this.$store.getters["ScoutStore/getPestConfigurationsNotForPlant"](this.currentPlantID);
                plantConfigs = [...new Set([...plantConfigs, ...nonPlantConfigs])];
            } else {
                // Get top 10 rated pests first
                let location = this.$store.getters["PestAndDiseaseStore/getLocations"].find(l => l.LocationID == this.editedScouting.LocationID);
                plantConfigs = plantConfigs.filter(p => p.ProductCategoryIDs.some(l => location.ProductCategories.some(pc => pc.ProductCategoryID == l)));
            }

            let configsWithValues = this.pestConfigurationsWithValues;
            let customConfigs = this.otherCustomPestConfigurations.map(c => { 
                return { 
                    PestConfigurationID: c.PestConfigurationID, 
                    PestClassification: "N/A", 
                    PestClassificationType: "N/A", 
                    Name: c.OtherName, 
                    ScoutingScoreScale: this.defaultScoutingScoreScale 
                }; });

            // some seriously cryptic stuff here... https://stackoverflow.com/questions/3629817/getting-a-union-of-two-arrays-in-javascript/10413596
            // let combined = [...new Set([...plantConfigs, ...configsWithValues, ...customConfigs])];
            // Ensure top10PlantConfigs are at the top, followed by the rest (excluding duplicates)
            let seen = new Set(top10PlantConfigs.map(pc => pc.PestConfigurationID));
            let rest = [...plantConfigs, ...configsWithValues, ...customConfigs]
              .filter(pc => !seen.has(pc.PestConfigurationID));
            let combined = [...top10PlantConfigs, ...rest];

            // Add Score property for sorting
            return combined.map(item => {
                let score = null;
                if (this.editedSpotCheckObservations && this.editedSpotCheckObservations[item.PestConfigurationID]) {
                    score = this.editedSpotCheckObservations[item.PestConfigurationID].Observation;
                }
                return {
                    ...item,
                    Score: score
                };
            });
        },
        otherCustomPestConfigurations() {
            let otherCustomObservations = JSON.parse(JSON.stringify(this.editedSpotCheck.ScoutingSpotCheckObservations.filter(o => o.PestConfigurationID < 0)));

            return otherCustomObservations;
        },
        pestConfigurationsWithValuesIDs() {
            // all of the pest configurations for which we have an observation value, whether or not the config is active, or for this plant
            let configIDsWithValues = [];
            
            // use the initial data, otherwise the elements will move around on the page as we edit things
            if (this.initialDataCopy.ScoutingSpotCheckObservations) {
                this.initialDataCopy.ScoutingSpotCheckObservations
                    .filter(o => o.Observation)
                    .forEach(o => {
                        configIDsWithValues.push(o.PestConfigurationID);
                    });
            }

            return configIDsWithValues;
        },
        defaultScoutingScoreScale() {
            let allConfigs = this.allPestConfigurations;
            let location = this.$store.getters["PestAndDiseaseStore/getLocations"].find(l => l.LocationID == this.editedScouting.LocationID);
            let filteredConfigs = allConfigs.filter(p => p.ProductCategoryIDs.some(l => location.ProductCategories.some(pc => pc.ProductCategoryID == l)));

            let filteredConfigsWithScoutingScoreScales = filteredConfigs.filter(p => p.ScoutingScoreScale != null);

            if (filteredConfigsWithScoutingScoreScales && filteredConfigsWithScoutingScoreScales.length > 0) {            
                return filteredConfigsWithScoutingScoreScales[0].ScoutingScoreScale;
            }

            return null;
        },
        pestConfigurationsWithValues() {
            let configIDsWithValues = this.pestConfigurationsWithValuesIDs;
            
            let configsWithValues = this.allPestConfigurations.filter(pc => configIDsWithValues.includes(pc.PestConfigurationID));

            return configsWithValues;
        },
        allPestConfigurations() {
            return this.$store.state.ScoutStore.pestConfigurations;
        },
        currentPlantID() {
            if (this.scouting && this.scouting.PlantID) {
                return this.scouting.PlantID;
            }

            // when we are adding a new scouting record, there is no existing scouting record to lookup, but we can use the plant selected on the parent page
            return this.$store.state.PestAndDiseaseStore.selectedPlant
        },
        scouting() {
            // this is the original data of the scouting that this spot check is for
            return this.$store.getters["ScoutStore/getScoutingByID"](this.editedSpotCheck.ScoutingID);
        }
    },
    methods: {
        async addNewOtherPest(otherPest) {
            let newObservation = this.createBlankObservation(null);
            newObservation.OtherName = otherPest.name;            
            newObservation.Observation = otherPest.observation;

            let promise = new Promise(function(resolve) {
                navigator.geolocation.getCurrentPosition(pos => {
                    let latitude = pos.coords.latitude;
                    let longitude = pos.coords.longitude;
                    resolve({ latitude, longitude})
                }, err => {                
                    console.log("Error Getting Location", err);
                    resolve({ latitude:0, longitude:0 });
                });
            });

            let result = await promise;

            newObservation.Latitude = result.latitude;
            newObservation.Longitude = result.longitude;

            this.editedSpotCheck.ScoutingSpotCheckObservations.push(newObservation);
            this.editedSpotCheckObservations[newObservation.PestConfigurationID] = newObservation;

            this.showOtherPest = false;
        },
        save() {
        
             // first we check our validation - if the form is not valid then we are not allowed to save
            if (!this.editSpotCheckValid) {
                return;
            }
            
            this.$emit("onSave", this.editedSpotCheck);

            // the usage of nextTick here is essentially to await the completion of the emitted onSave event in the parent
            // this is messy, and possibly not really recommended - I originally took the idea from here: https://forum.vuejs.org/t/do-something-after-emit-has-finished-successful/10663/3
            // if there is a better way to handle this, we should change it
            this.$nextTick(() => {
                // the initialData prop should have changed after the successful running of the emitted event handler
                // so that we can correctly determine if there have been further changes, we need to update what we are storing as the "original" value
                this.resetToInitialData();
            });            
        },
        cancel() {
            // just reset back to the initial state (this.initialData could have changed since then - this is OK)
            this.resetToInitialData();
        },
        checkForUnsavedChanges(newNumber) {
            // there are unsaved changes if we have diverged from the original
            let unsavedChanges = (JSON.stringify(this.initialDataCopy) != JSON.stringify(this.editedSpotCheck));

            // if (unsavedChanges) {
            //     console.log(`there are unsaved changes:`, unsavedChanges)
            //     console.log(`JSON.stringify(this.initialDataCopy):`, JSON.stringify(this.initialDataCopy));
            //     console.log(`JSON.stringify(this.editedSpotCheck):`, JSON.stringify(this.editedSpotCheck));
            // }

            this.$emit("onCheckUnsaved", {
                newNumber: newNumber,
                currentlySelectedNumber: this.editedSpotCheck.Number,
                unsavedChanges: unsavedChanges
            });
        },
        showAllPest() {
            this.showAll = !this.showAll;
        },
        createBlankObservation(pestConfigurationID) {
            return {
                ScoutingSpotCheckObservationID: -1,
                ScoutingSpotCheckID: this.editedSpotCheck.ScoutingSpotCheckID,
                PestConfigurationID: pestConfigurationID || this.negativeIndexForObservationsWithoutPestConfigurationData--,
                Observation: null,
                Time: null,
                Notes: null,
                OtherName: null,
                Size: null
            }
        },
        initialiseSpotCheckObservations() {
            this.editedSpotCheck.ScoutingSpotCheckObservations = [];
        },
        notesChanged(event) {
            let existingObservation = this.editedSpotCheck.ScoutingSpotCheckObservations.find(o => o.PestConfigurationID === event.pestConfigurationID);

            if (existingObservation) {
                existingObservation.Notes = event.notes;
                existingObservation.NotesDate = moment().toISOString(true);
                // Clear the Update By.  Will be set on the server with the update
                existingObservation.NotesBy = null;
                this.editedSpotCheckObservations[event.pestConfigurationID] = existingObservation;
            }
            else {
                let newObservation = Object.assign(this.createBlankObservation(event.pestConfigurationID), { Notes: event.notes, NotesDate: moment().toISOString(true) });
                this.editedSpotCheck.ScoutingSpotCheckObservations.push(newObservation);
                this.editedSpotCheckObservations[event.pestConfigurationID] = newObservation;
            }
        },
        sizeChanged(event) {
            if (!this.editedSpotCheck.ScoutingSpotCheckObservations) {
                this.initialiseSpotCheckObservations();
            }

            let existingObservation = this.editedSpotCheck.ScoutingSpotCheckObservations.find(o => o.PestConfigurationID === event.pestConfigurationID);

            if (existingObservation) {
                existingObservation.Size = event.size;
                this.editedSpotCheckObservations[event.pestConfigurationID] = existingObservation;
            }
            else {
                let newObservation = Object.assign(this.createBlankObservation(event.pestConfigurationID), { Size: event.size });
                this.editedSpotCheck.ScoutingSpotCheckObservations.push(newObservation);
                this.editedSpotCheckObservations[event.pestConfigurationID] = newObservation;
            }
        },
        gPSChanged(event) {
            if (!this.editedSpotCheck.ScoutingSpotCheckObservations) {
                this.initialiseSpotCheckObservations();
            }

            let existingObservation = this.editedSpotCheck.ScoutingSpotCheckObservations.find(o => o.PestConfigurationID === event.pestConfigurationID);

            if (existingObservation) {
                existingObservation.Latitude = event.gps.Latitude;
                existingObservation.Longitude = event.gps.Longitude;
                this.editedSpotCheckObservations[event.pestConfigurationID] = existingObservation;
            }
            else {
                let newObservation = Object.assign(this.createBlankObservation(event.pestConfigurationID), { Latitude: event.gps.Latitude, Longitude: event.gps.Longitude });
                this.editedSpotCheck.ScoutingSpotCheckObservations.push(newObservation);
                this.editedSpotCheckObservations[event.pestConfigurationID] = newObservation;
            } 
        },
        async observationChanged(event) {
            if (this.processing) {
                return;
            }

            this.processing = true;

            if (!this.editedSpotCheck.ScoutingSpotCheckObservations) {
                this.initialiseSpotCheckObservations();
            }

            let existingObservation = this.editedSpotCheck.ScoutingSpotCheckObservations.find(o => o.PestConfigurationID === event.pestConfigurationID);

            if (existingObservation) {
                existingObservation.Observation = event.observation;                
                existingObservation.Time = moment().format("HH:mm:00");

                if (existingObservation.Latitude == null || existingObservation.Longitude == null) {
                    let promise = new Promise(function(resolve) {
                        navigator.geolocation.getCurrentPosition(pos => {
                            let latitude = pos.coords.latitude;
                            let longitude = pos.coords.longitude;
                            resolve({ latitude, longitude})
                        }, err => {                
                            console.log("Error Getting Location", err);
                            resolve({ latitude:0, longitude:0 });
                        });
                    });

                    let result = await promise;

                    existingObservation.Latitude = result.latitude;
                    existingObservation.Longitude = result.longitude;
                }

                this.editedSpotCheckObservations[event.pestConfigurationID] = existingObservation;
            }
            else {
                let newObservation = Object.assign(this.createBlankObservation(event.pestConfigurationID), { Observation: event.observation, Time: moment().format("HH:mm:00") });

                let promise = new Promise(function(resolve) {
                    navigator.geolocation.getCurrentPosition(pos => {
                        let latitude = pos.coords.latitude;
                        let longitude = pos.coords.longitude;
                        resolve({ latitude, longitude})
                    }, err => {                
                        console.log("Error Getting Location", err);
                        resolve({ latitude:0, longitude:0 });
                    });
                });

                let result = await promise;

                newObservation.Latitude = result.latitude;
                newObservation.Longitude = result.longitude;              

                this.editedSpotCheck.ScoutingSpotCheckObservations.push(newObservation);
                this.editedSpotCheckObservations[event.pestConfigurationID] = newObservation;
            }

            // rerun the validation, it seems to be having issues doing so on its own
            // it's in a nextTick because it works this way... don't ask me why
            this.$nextTick(() => {
                this.$refs.editSpotCheckForm.validate();
                this.processing = false;
            });            
        },
        resetToInitialData() {
            let slightlyEditedCopyOfInitialData = JSON.parse(JSON.stringify(this.initialData));

            // we need some way of referring to the "other" observations that do not have a pest configuration related to them
            // we will use a unique, negative ID for this purpose, in the PestConfigurationID field
            slightlyEditedCopyOfInitialData.ScoutingSpotCheckObservations.forEach(o => {
                if (!o.PestConfigurationID) {
                    o.PestConfigurationID = this.negativeIndexForObservationsWithoutPestConfigurationData--;
                }
            });

            this.initialDataCopy = JSON.parse(JSON.stringify(slightlyEditedCopyOfInitialData));
            this.editedSpotCheck = slightlyEditedCopyOfInitialData;

            this.setupEditedSpotCheckObservations();
        },
        /**
         * This just gives a normalised view of the observations array, which is easier to access
         * without needing constant find() or filter() calls.
         */
        setupEditedSpotCheckObservations() {
            let indexed = {};

            // start with empty objects for all pests
            this.allPestConfigurations.forEach((item) => {
                indexed[item.PestConfigurationID] = {
                    Observation: null,
                    Time: null,
                    Notes: null,
                    Size: null
                };
            });

            // now overwrite the empty objects with actual data where there is some
            if (this.editedSpotCheck.ScoutingSpotCheckObservations) {
                this.editedSpotCheck.ScoutingSpotCheckObservations.forEach((item) => {
                    // note that this maintains the reference to the original data - this means we should edit with care wherever this is used
                    indexed[item.PestConfigurationID] = item;
                });
            }
            
            this.editedSpotCheckObservations = indexed;            
        },
    },
    async created() {
        this.resetToInitialData();
        this.imageAuthToken = await this.$store.dispatch("UserStore/acquireImageAuthToken");
    },
    async mounted() {
        this.$root.$on("saveScoutingSpotCheck", (spotCheckNumber) => {
            if (this.editedSpotCheck.Number === spotCheckNumber) {
                // if this event is for the current component (the spot check number matches), then we need to pass the data to save back to our parent
                this.save();
            }
        });

        this.$root.$on("cancelScoutingSpotCheck", (spotCheckNumber) => {
            if (this.editedSpotCheck.Number === spotCheckNumber) {
                // if this event is for the current component (the spot check number matches), then we need to pass the data to save back to our parent
                this.cancel();
            }
        });

        this.$root.$on("tryChangeSelectedSpotCheck", (options) => {
            // we need to check for unsaved changes if the currently selected number is the one for this component - otherwise it's not this component's problem
            if (this.editedSpotCheck.Number === options.currentlySelectedNumber) {
                this.checkForUnsavedChanges(options.newNumber);
            }
        });

        try {
            const result = await pestConfigurationApi.getPestObservationsByLocationID(this.editedScouting.LocationID);
            this.top10Observations = Array.isArray(result) ? result : [];
        } catch (e) {
            console.error('Failed to fetch top10Observations:', e);
            this.top10Observations = [];
        }
    }
}
</script>

<style>
.pest-configs-header {
   padding-left: 2px !important;
   padding-right: 2px !important;
}
</style>